import { Component, OnInit } from '@angular/core';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { MatterFileType } from '@models/matter';
import { ICON } from '@core/util/icon';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { cloneDeep } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { createId } from '@core/util/file';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BusinessDispatch, BusinessState } from '@state/business';
import { combineLatest, firstValueFrom, Observable, take } from 'rxjs';
import { Select } from '@ngxs/store';
import { TrustLedgerSelectors } from '@state/global/trust-ledger/trust_ledger';
import {
	TrustLedgerDefault,
	LedgerType,
	TrustLedgerItemType,
} from '@models/trustLedger';
import {
	BusinessSettings,
	BusinessTrustLedgerSettings,
	SoftwareTransactionFeeDisplayLocation,
} from '@models/businessSettings/settings';
import { TestHooks } from './trust-ledger-defaults.hooks';

const fileTypeOptions: RadioGroupItem<MatterFileType>[] = [
	{
		value: MatterFileType.SALE,
		label: 'Sale',
	},
	{
		value: MatterFileType.PURCHASE,
		label: 'Purchase',
	},
	{
		value: MatterFileType.REFINANCE,
		label: 'Refinance',
	},
];

const showFirstOptions = [
	{ name: 'Payments at the top', value: false },
	{ name: 'Receipts at the top', value: true },
];

@UntilDestroy()
@Component({
	selector: 'app-trust-ledger-defaults',
	templateUrl: './trust-ledger-defaults.component.html',
	standalone: false,
})
export class TrustLedgerDefaultsComponent implements OnInit {
	testHooks = TestHooks;
	public filteredOrderedPayments = [];
	public filteredOrderedReceipts = [];

	public trustLedgerSale: TrustLedgerDefault[];
	public trustLedgerPurchase: TrustLedgerDefault[];
	public trustLedgerRefinance: TrustLedgerDefault[];

	public fileTypesOptions = fileTypeOptions;
	public selectedFileType = MatterFileType.SALE;
	public ICON = ICON;
	public amount: number;
	public description: string;
	public businessSettings: BusinessSettings;
	public showFirstOptions = showFirstOptions;
	public selectedFirstOption;

	@Select(BusinessState.getDefaultSettings)
	businessSettings$: Observable<BusinessSettings>;

	@Select(TrustLedgerSelectors.getDefaultTrustLedgerSale)
	trustLedgerSale$: Observable<TrustLedgerDefault[]>;

	@Select(TrustLedgerSelectors.getDefaultTrustLedgerPurchase)
	trustLedgerPurchase$: Observable<TrustLedgerDefault[]>;

	@Select(TrustLedgerSelectors.getDefaultTrustLedgerRefinance)
	trustLedgerRefinance$: Observable<TrustLedgerDefault[]>;

	constructor(
		private matDialog: MatDialog,
		private businessDispatch: BusinessDispatch,
	) {}

	ngOnInit(): void {
		combineLatest([
			this.trustLedgerSale$,
			this.trustLedgerPurchase$,
			this.trustLedgerRefinance$,
			this.businessSettings$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(
				async ([
					trustLedgerSale,
					trustLedgerPurchase,
					trustLedgerRefinance,
					businessSettings,
				]) => {
					this.trustLedgerSale = trustLedgerSale;
					this.trustLedgerPurchase = trustLedgerPurchase;
					this.trustLedgerRefinance = trustLedgerRefinance;
					await this.setFilteredPayments(this.selectedFileType);
					this.setFilteredReceipts(this.selectedFileType);
					this.businessSettings = cloneDeep(businessSettings);

					this.setShowFirstOption();
				},
			);
	}

	async setFilteredPayments(matterType: MatterFileType): Promise<void> {
		const businessSettings = await firstValueFrom(
			this.businessSettings$.pipe(take(1)),
		);
		const showSoftwareTransactionFeeInTl =
			businessSettings?.softwareTransactionFee?.displayIn ===
			SoftwareTransactionFeeDisplayLocation.TRUST_LEDGER;

		switch (matterType) {
			case MatterFileType.SALE:
				this.filteredOrderedPayments = this.trustLedgerSale?.filter(
					(x) =>
						x.fileType === this.selectedFileType &&
						x.ledgerType === LedgerType.PAYMENT &&
						(showSoftwareTransactionFeeInTl ||
							x.type !== TrustLedgerItemType.CLOSER_FEE),
				);
				break;
			case MatterFileType.PURCHASE:
				this.filteredOrderedPayments = this.trustLedgerPurchase?.filter(
					(x) =>
						x.fileType === this.selectedFileType &&
						x.ledgerType === LedgerType.PAYMENT &&
						(showSoftwareTransactionFeeInTl ||
							x.type !== TrustLedgerItemType.CLOSER_FEE),
				);
				break;
			case MatterFileType.REFINANCE:
				this.filteredOrderedPayments = this.trustLedgerRefinance?.filter(
					(x) =>
						x.fileType === this.selectedFileType &&
						x.ledgerType === LedgerType.PAYMENT &&
						(showSoftwareTransactionFeeInTl ||
							x.type !== TrustLedgerItemType.CLOSER_FEE),
				);
				break;
		}
	}

	setFilteredReceipts(matterType: MatterFileType): void {
		switch (matterType) {
			case MatterFileType.SALE:
				this.filteredOrderedReceipts = this.trustLedgerSale?.filter(
					(x) =>
						x.fileType === this.selectedFileType &&
						x.ledgerType === LedgerType.RECEIPT,
				);
				break;
			case MatterFileType.PURCHASE:
				this.filteredOrderedReceipts = this.trustLedgerPurchase?.filter(
					(x) =>
						x.fileType === this.selectedFileType &&
						x.ledgerType === LedgerType.RECEIPT,
				);
				break;
			case MatterFileType.REFINANCE:
				this.filteredOrderedReceipts = this.trustLedgerRefinance?.filter(
					(x) =>
						x.fileType === this.selectedFileType &&
						x.ledgerType === LedgerType.RECEIPT,
				);
				break;
		}
	}

	addReceipt(): void {
		const data: TrustLedgerDefault = {
			id: createId(),
			amount: 0,
			description: '',
			ledgerType: LedgerType.RECEIPT,
			fileType: this.selectedFileType,
			index: this.filteredOrderedReceipts?.length ?? 0,
			type: TrustLedgerItemType.CUSTOM,
			date: '',
		};

		this.updateReceipt(data);
		if (this.filteredOrderedReceipts?.length > 0) {
			this.businessDispatch.SortDefaultReceipts(
				[
					...this.filteredOrderedReceipts.map(
						(receipt: TrustLedgerDefault) => receipt.id,
					),
					data.id,
				],
				this.selectedFileType,
			);
		} else {
			this.businessDispatch.SortDefaultReceipts(
				[data.id],
				this.selectedFileType,
			);
		}
	}

	updateReceipt(receipt: TrustLedgerDefault): void {
		this.businessDispatch.SetDefaultTrustLedgerReceipt(
			receipt,
			this.selectedFileType,
		);
	}

	addPayment(): void {
		const data: TrustLedgerDefault = {
			id: createId(),
			amount: 0,
			description: '',
			ledgerType: LedgerType.PAYMENT,
			fileType: this.selectedFileType,
			index: this.filteredOrderedPayments?.length ?? 0,
			type: TrustLedgerItemType.CUSTOM,
			date: '',
		};

		this.updatePayment(data);
		if (this.filteredOrderedPayments?.length > 0) {
			this.businessDispatch.SortDefaultPayments(
				[
					...this.filteredOrderedPayments.map(
						(payment: TrustLedgerDefault) => payment.id,
					),
					data.id,
				],
				this.selectedFileType,
			);
		} else {
			this.businessDispatch.SortDefaultPayments(
				[data.id],
				this.selectedFileType,
			);
		}
	}

	updatePayment(payment: TrustLedgerDefault): void {
		this.businessDispatch.SetDefaultTrustLedgerPayment(
			payment,
			this.selectedFileType,
		);
	}

	trackByFn(index: number, item: TrustLedgerDefault): string {
		return item.id;
	}

	_dropPayment(event: CdkDragDrop<string[]>): void {
		const reOrderedPayments = cloneDeep(this.filteredOrderedPayments);
		moveItemInArray(reOrderedPayments, event.previousIndex, event.currentIndex);
		const orderedPaymentIds = reOrderedPayments?.map(
			(x: TrustLedgerDefault) => x.id,
		);
		this.businessDispatch.SortDefaultPayments(
			orderedPaymentIds,
			this.selectedFileType,
		);
	}

	_dropReceipt(event: CdkDragDrop<string[]>): void {
		const reOrderedReceipts = cloneDeep(this.filteredOrderedReceipts);
		moveItemInArray(reOrderedReceipts, event.previousIndex, event.currentIndex);
		const orderedReceiptIds = reOrderedReceipts?.map(
			(x: TrustLedgerDefault) => x.id,
		);
		this.businessDispatch.SortDefaultReceipts(
			orderedReceiptIds,
			this.selectedFileType,
		);
	}

	setPaymentDescription(
		newPayment: TrustLedgerDefault,
		description: string,
	): void {
		const _newPayment = {
			...newPayment,
			description,
		};
		this.updatePayment(_newPayment);
	}

	setReceiptDescription(
		newReceipt: TrustLedgerDefault,
		description: string,
	): void {
		const _newReceipt = {
			...newReceipt,
			description,
		};
		this.updateReceipt(_newReceipt);
	}

	setPaymentAmount(newPayment: TrustLedgerDefault, amount: number): void {
		const _newPayment = {
			...newPayment,
			amount,
		};
		this.updatePayment(_newPayment);
	}

	setReceiptAmount(newReceipt: TrustLedgerDefault, amount: number): void {
		const _newReceipt = {
			...newReceipt,
			amount,
		};
		this.updateReceipt(_newReceipt);
	}

	async changeFileType(fileType: MatterFileType): Promise<void> {
		this.selectedFileType = fileType;
		await this.setFilteredPayments(fileType);
		this.setFilteredReceipts(fileType);
		this.setShowFirstOption();
	}

	deletePayment(id: string): void {
		this.businessDispatch.DeleteDefaultTrustLedgerPayment(
			id,
			this.selectedFileType,
		);
	}

	deleteReceipt(id: string): void {
		this.businessDispatch.DeleteDefaultTrustLedgerReceipt(
			id,
			this.selectedFileType,
		);
	}

	onSelectShowFirst(value) {
		this.businessSettings.trustLedger.showReceiptsFirst[this.selectedFileType] =
			value.result.value;
		this.setShowFirstOption();
		this.businessDispatch.UpdateDefaultSettings({
			trustLedger: {
				showReceiptsFirst: this.businessSettings.trustLedger.showReceiptsFirst,
			} as BusinessTrustLedgerSettings,
		});
	}

	setShowFirstOption() {
		const selectedOption =
			this.businessSettings.trustLedger.showReceiptsFirst[
				this.selectedFileType
			];
		this.selectedFirstOption = selectedOption
			? 'Receipts at the top'
			: 'Payments at the top';
	}
}
